import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { TransferenciasService, ContasService, fmtMoeda, fmtData, mesAtual } from '../services';

const initForm = { conta_origem_id: '', conta_destino_id: '', valor: '', taxa: '', data: new Date().toISOString().split('T')[0], descricao: 'Transferência entre contas' };

export default function Transferencias() {
  const [transferencias, setTransferencias] = useState([]);
  const [contas, setContas] = useState([]);
  const [mes, setMes] = useState(mesAtual());
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(initForm);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    TransferenciasService.listar({ mes }).then(setTransferencias).finally(() => setLoading(false));
  }, [mes]);

  useEffect(() => {
    load();
    ContasService.listar().then(setContas);
  }, [load]);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setErro('');
    try {
      await TransferenciasService.criar(form);
      setModal(false); setForm(initForm); load();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta transferência? Os saldos serão revertidos.')) return;
    try { await TransferenciasService.excluir(id); load(); }
    catch (err) { alert(err.response?.data?.erro || 'Erro'); }
  };

  const totalTransferido = transferencias.reduce((s, t) => s + parseFloat(t.valor), 0);

  return (
    <Layout title="Transferências">
      <div className="flex-between mb-3">
        <div className="flex-gap">
          <input type="month" value={mes} onChange={e => setMes(e.target.value)}
            style={{ padding: '7px 10px', border: '1.5px solid var(--gray-200)', borderRadius: 7, fontSize: 13 }} />
          <span className="badge badge-primary">{fmtMoeda(totalTransferido)} transferido</span>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(initForm); setErro(''); setModal(true); }}>
          + Nova Transferência
        </button>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-wrap"><div className="spinner" /></div>
        ) : transferencias.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔄</div>
            <p>Nenhuma transferência no período.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Descrição</th><th>De</th><th>Para</th><th>Data</th><th>Taxa</th><th>Valor</th><th></th></tr>
              </thead>
              <tbody>
                {transferencias.map(t => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 500, fontSize: 13 }}>{t.descricao}</td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span className="color-dot" style={{ background: t.conta_origem_cor }} />
                        {t.conta_origem_nome}
                      </span>
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span className="color-dot" style={{ background: t.conta_destino_cor }} />
                        {t.conta_destino_nome}
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>{fmtData(t.data)}</td>
                    <td style={{ fontSize: 13 }}>{parseFloat(t.taxa) > 0 ? fmtMoeda(t.taxa) : '—'}</td>
                    <td style={{ fontWeight: 600 }}>{fmtMoeda(t.valor)}</td>
                    <td>
                      <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--danger)' }}
                        onClick={() => handleDelete(t.id)}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal modal-sm">
            <div className="modal-header">
              <span className="modal-title">Nova Transferência</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                {erro && <div className="alert alert-danger">{erro}</div>}
                <div className="form-group">
                  <label className="form-label">Descrição</label>
                  <input className="form-control" value={form.descricao}
                    onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Conta de origem *</label>
                  <select className="form-control" required value={form.conta_origem_id}
                    onChange={e => setForm(p => ({ ...p, conta_origem_id: e.target.value }))}>
                    <option value="">Selecionar...</option>
                    {contas.map(c => <option key={c.id} value={c.id}>{c.icone} {c.nome} ({fmtMoeda(c.saldo_atual)})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Conta de destino *</label>
                  <select className="form-control" required value={form.conta_destino_id}
                    onChange={e => setForm(p => ({ ...p, conta_destino_id: e.target.value }))}>
                    <option value="">Selecionar...</option>
                    {contas.filter(c => c.id !== parseInt(form.conta_origem_id)).map(c =>
                      <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
                  </select>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Valor (R$) *</label>
                    <input type="number" step="0.01" min="0.01" className="form-control" required value={form.valor}
                      onChange={e => setForm(p => ({ ...p, valor: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Taxa (R$)</label>
                    <input type="number" step="0.01" className="form-control" value={form.taxa}
                      onChange={e => setForm(p => ({ ...p, taxa: e.target.value }))} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Data *</label>
                  <input type="date" className="form-control" required value={form.data}
                    onChange={e => setForm(p => ({ ...p, data: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Salvando...' : 'Transferir'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
